import { jsPDF } from 'jspdf';
import type { CreditCardAuthorizationFormData, Invite } from '../types';
import { resolveSignatureDataUrl } from './signatureUtils';

export async function generateCreditCardPdf(
  formData: CreditCardAuthorizationFormData,
  invite?: Invite | null,
  applicationId: string = 'PENDING',
  auditLog: any[] = []
): Promise<string> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const ml = 25.4;
  const mr = 25.4;
  const uw = W - ml - mr;

  let y = 20;

  const font = (style: 'normal' | 'bold' | 'italic', size: number) => {
    pdf.setFont('helvetica', style);
    pdf.setFontSize(size);
  };

  const tx = (text: string, x: number, yPos: number, options?: any) => {
    pdf.text(text, x, yPos, options);
  };

  const hline = (yPos: number) => {
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(ml, yPos, W - mr, yPos);
  };

  const fieldRow = (label: string, value: string, x: number, yPos: number, endX: number) => {
    font('bold', 7.5);
    pdf.setTextColor(100, 100, 100);
    tx(label, x, yPos);
    
    const lx = x + pdf.getTextWidth(label) + 1;
    font('bold', 8.5);
    pdf.setTextColor(0, 0, 0);
    const valStr = value || '';
    tx(valStr, lx, yPos);

    pdf.setDrawColor(55, 65, 81);
    pdf.setLineWidth(0.2);
    pdf.line(lx - 1, yPos + 1.5, endX, yPos + 1.5);
  };

  const checkbox = (x: number, yPos: number, checked: boolean) => {
    pdf.setDrawColor(55, 65, 81);
    pdf.setLineWidth(0.3);
    pdf.rect(x, yPos - 3.5, 3.5, 3.5);
    if (checked) {
      pdf.setLineWidth(0.6);
      pdf.line(x + 0.8, yPos - 1.5, x + 1.5, yPos - 0.5);
      pdf.line(x + 1.5, yPos - 0.5, x + 2.8, yPos - 3);
    }
  };

  // ── Header ──────────────────────────────────────────────
  try {
    const resp = await fetch(
      'https://firebasestorage.googleapis.com/v0/b/dataseeker-12378.firebasestorage.app/o/template-images%2F1770677341197_logo.png?alt=media&token=34c46eaa-ef71-403d-99e0-029e3462db45'
    );
    const blob = await resp.blob();
    const b64 = await new Promise<string>((res) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result as string);
      reader.readAsDataURL(blob);
    });
    const logoW = 30;
    const logoH = logoW * (81 / 489);
    pdf.addImage(b64, 'PNG', W / 2 - logoW / 2, y, logoW, logoH);
    y += logoH + 4;
  } catch {
    font('bold', 12);
    tx('ARMELLINI LOGISTICS', W / 2, y + 8, { align: 'center' });
    y += 12;
  }

  font('bold', 14);
  pdf.setTextColor(15, 23, 42);
  tx('CREDIT CARD AUTHORIZATION FORM', W / 2, y, { align: 'center' });
  y += 8;
  hline(y);
  y += 10;

  // ── Authorization Text ──────────────────────────────────────────────
  font('bold', 9);
  pdf.setTextColor(15, 23, 42);
  const cardHolderFullName = [formData.card.cardHolderFirstName, formData.card.cardHolderLastName].filter(Boolean).join(' ');
  const authText = `I, ${cardHolderFullName}, hereby authorize Armellini Logistics to charge my credit card. As the Credit Card holder; I also authorize Armellini Logistics to charge my credit card for future purchases up until the date stated below.`;
  const authLines = pdf.splitTextToSize(authText, uw);
  pdf.text(authLines, ml, y);
  y += (authLines.length * 4) + 6;

  // ── Valid Until & Initials ──────────────────────────────────────────────
  const s2 = ml + uw * 0.5;
  fieldRow('Authorization Valid Until: ', `${formData.authorization.validUntilMonth || '__'} / ${formData.authorization.validUntilYear || '____'}`, ml, y, s2 - 10);
  fieldRow('Initials here: ', formData.authorization.initials, s2, y, W - mr);
  y += 10;

  // ── Policy Text ──────────────────────────────────────────────
  const policyText = "I understand that I will NOT receive a CASH refund. I also agree to follow all credit policies and requirements as stated on all of my purchased invoices; we are not responsible for any carrier related problems.";
  const policyLines = pdf.splitTextToSize(policyText, uw);
  pdf.text(policyLines, ml, y);
  y += (policyLines.length * 4) + 6;

  // ── Card Types ──────────────────────────────────────────────
  const CARD_TYPES = [
    { value: 'visa', label: 'VISA' },
    { value: 'mastercard', label: 'MASTERCARD' },
    { value: 'amex', label: 'AMERICAN EXPRESS' },
    { value: 'discover', label: 'DISCOVER' },
    { value: 'debit', label: 'DEBIT CARD' },
  ];
  let cx = ml + 6;
  CARD_TYPES.forEach(ct => {
    checkbox(cx, y, formData.card.cardType === ct.value);
    font('bold', 8);
    tx(ct.label, cx + 5, y);
    y += 6;
  });
  y += 4;

  // ── Card Details Fields ──────────────────────────────────────────────
  fieldRow('Company Name: ', formData.card.companyName, ml, y, W - mr);
  y += 8;

  const hw = ml + uw * 0.5;
  fieldRow('Credit Card #: ', formData.card.cardNumber, ml, y, hw - 5);
  fieldRow('Exp: ', `${formData.card.expMonth} / ${formData.card.expYear}`, hw + 5, y, W - mr);
  y += 8;

  fieldRow('CVV # (3 or 4 digits): ', formData.card.cvv, ml, y, hw - 5);
  fieldRow('Telephone: ', formData.card.telephone, hw + 5, y, W - mr);
  y += 8;

  fieldRow('Credit Card Billing Address: ', formData.card.billingAddress, ml, y, W - mr);
  y += 8;

  const w3_1 = ml + uw * 0.4;
  const w3_2 = ml + uw * 0.7;
  fieldRow('City: ', formData.card.city, ml, y, w3_1 - 5);
  fieldRow('ST: ', formData.card.state, w3_1 + 5, y, w3_2 - 5);
  fieldRow('ZipCode: ', formData.card.zipCode, w3_2 + 5, y, W - mr);
  y += 8;

  if (formData.card.invoiceOrderNumber) {
    fieldRow('Invoice / Order #: ', formData.card.invoiceOrderNumber, ml, y, hw - 5);
  }
  if (formData.card.totalAmount) {
    let tx_pos = formData.card.invoiceOrderNumber ? hw + 5 : ml;
    let ex_pos = formData.card.invoiceOrderNumber ? W - mr : hw - 5;
    fieldRow('Total $: ', formData.card.totalAmount, tx_pos, y, ex_pos);
  }
  if (formData.card.invoiceOrderNumber || formData.card.totalAmount) y += 8;

  y += 4;
  hline(y);
  y += 12;

  // ── Signature Section ──────────────────────────────────────────────
  const dw = (uw - 20) / 3;
  const s3_1 = ml;
  const s3_2 = ml + dw + 10;
  const s3_3 = ml + (dw * 2) + 20;

  // Names line
  pdf.setDrawColor(55, 65, 81);
  pdf.setLineWidth(0.3);
  pdf.line(s3_1, y, s3_1 + dw, y);
  pdf.line(s3_2, y, s3_2 + dw, y);
  pdf.line(s3_3, y, s3_3 + dw, y);

  font('bold', 9);
  pdf.setTextColor(0, 0, 0);
  tx(formData.card.cardHolderFirstName || '', s3_1 + (dw/2), y - 2, { align: 'center' });
  tx(formData.card.cardHolderLastName || '', s3_2 + (dw/2), y - 2, { align: 'center' });
  tx(formData.authorization.signedDate || '', s3_3 + (dw/2), y - 2, { align: 'center' });

  font('bold', 7.5);
  pdf.setTextColor(71, 85, 105);
  tx('Print Card Holder Name', s3_1 + (dw/2), y + 4, { align: 'center' });
  tx('Last Name', s3_2 + (dw/2), y + 4, { align: 'center' });
  tx('Date', s3_3 + (dw/2), y + 4, { align: 'center' });

  y += 24;

  // Real Signature
  font('bold', 9);
  pdf.setTextColor(0, 0, 0);
  tx('Authorized Signature X', ml, y);
  
  const sigStartX = ml + pdf.getTextWidth('Authorized Signature X') + 4;
  const sigEndX = W - mr;
  
  if (formData.authorization.signature) {
    const sigImgW = (sigEndX - sigStartX) * 0.7;
    const sigUrl = resolveSignatureDataUrl(formData.authorization.signature, sigImgW, 12);
    if (sigUrl) {
      pdf.addImage(sigUrl, 'PNG', sigStartX + 10, y - 10, sigImgW, 12);
    }
  }

  pdf.setDrawColor(55, 65, 81);
  pdf.setLineWidth(0.3);
  pdf.line(sigStartX, y + 1.5, sigEndX, y + 1.5);

  y = H - 35;
  const footerBoxY = y;
  
  // ── Disclaimer Box ──────────────────────────────────────────────
  pdf.setDrawColor(226, 232, 240);
  pdf.setFillColor(248, 250, 252);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(ml, footerBoxY, uw, 18, 2, 2, 'FD');
  
  font('normal', 7.5);
  pdf.setTextColor(71, 85, 105);
  const disclaimerText = "Please read carefully and complete the entire form — this will help protect against fraud. Armellini Logistics will not charge your Credit Card without the proper authorized written approval. There is a 3% Convenience fee on all credit cards.";
  const discLines = pdf.splitTextToSize(disclaimerText, uw - 8);
  pdf.text(discLines, ml + 4, footerBoxY + 5);

  font('bold', 7.5);
  pdf.setTextColor(2, 132, 199);
  tx('Complete and sign this form, email back to: Adubner@armellini.com', ml + 4, footerBoxY + 14);

  // ── Footer ──────────────────────────────────────────────
  y = H - 10;
  hline(y - 3);
  
  font('normal', 7);
  pdf.setTextColor(100, 116, 139);
  tx('P.O.BOX 520646 Miami, Florida 33152 — Phone: (786) 547-9894 — Adubner@armellini.com', W/2, y, { align: 'center' });
  if (applicationId && applicationId !== 'PENDING') {
    font('normal', 6.5);
    tx(`Ref: ${applicationId}`, W/2, y + 4, { align: 'center' });
  }

  // ── Page 2: Audit Log ──────────────────────────────────────────────
  if (auditLog && auditLog.length > 0) {
    pdf.addPage();
    let ay = 14;
    
    // ── Logo ─────────────────────────────────────────────────────────────
    try {
      const resp = await fetch(
        'https://firebasestorage.googleapis.com/v0/b/dataseeker-12378.firebasestorage.app/o/template-images%2F1770677341197_logo.png?alt=media&token=34c46eaa-ef71-403d-99e0-029e3462db45'
      );
      const blob = await resp.blob();
      const b64 = await new Promise<string>((res) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.readAsDataURL(blob);
      });
      const logoW = 30;
      const logoH = logoW * (81 / 489);
      pdf.addImage(b64, 'PNG', ml, ay, logoW, logoH);
    } catch {
      font('bold', 12);
      tx('ARMELLINI LOGISTICS', ml, ay + 8);
    }

    font('normal', 16);
    pdf.setTextColor(100);
    tx('Audit Log', W - mr - pdf.getTextWidth('Audit Log') - 10, ay + 8);
    pdf.setTextColor(0);

    ay += 18;
    font('bold', 9);
    tx(`Envelope ID: ${applicationId}`, ml, ay);

    font('normal', 8);
    pdf.setTextColor(0, 100, 255);
    tx('Verify', W - mr - pdf.getTextWidth('Verify') - 10, ay);
    pdf.setTextColor(0);

    ay += 4;
    hline(ay);
    ay += 6;

    const docName = `Credit Card Authorization - ${formData.card.companyName || formData.card.cardHolderLastName}`;
    font('normal', 8);
    tx(docName, ml, ay);

    // Right side box with hashes
    const boxW = 85;
    const boxX = W - mr - boxW;

    pdf.setDrawColor(200);
    pdf.setFillColor(248, 249, 250);
    pdf.setLineWidth(0.5);
    pdf.rect(boxX, ay - 4, boxW, 26, 'FD'); // Filled and stroked

    const hashgen = () => Array.from({length: 64}, () => Math.floor(Math.random()*16).toString(16)).join('');
    const hashStr1 = hashgen();
    const hashStr2 = hashgen();

    font('bold', 8);
    tx('Original SHA256:', boxX + 4, ay);
    font('normal', 6);

    // Split the hash so it wraps inside the box
    const hashLines1 = pdf.splitTextToSize(hashStr1, boxW - 8);
    tx(hashLines1, boxX + 4, ay + 4);

    font('bold', 8);
    tx('Result SHA256:', boxX + 4, ay + 10);
    font('normal', 6);
    const hashLines2 = pdf.splitTextToSize(hashStr2, boxW - 8);
    tx(hashLines2, boxX + 4, ay + 14);

    font('bold', 8);
    const generatedDate = new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeStyle: 'short', timeZone: 'America/New_York' }).format(new Date());
    tx(`Generated at:`, boxX + 4, ay + 19);
    font('normal', 7);
    tx(generatedDate, boxX + 4 + pdf.getTextWidth('Generated at: ') + 1, ay + 19);

    ay += 10;
    font('bold', 9);
    tx(invite?.customerEmail || '', ml, ay);
    ay += 6;
    font('normal', 8);
    tx('Email verification: Verified', ml, ay);
    ay += 5;
    const lastEvent = auditLog[auditLog.length - 1];
    tx(`IP: ${lastEvent?.ip || 'Unknown'}`, ml, ay);
    ay += 5;
    tx(`Session ID: ${Array.from({length: 32}, () => Math.floor(Math.random()*16).toString(16)).join('')}`, ml, ay);
    ay += 5;

    const uaLines = pdf.splitTextToSize(`User agent: ${lastEvent?.userAgent || 'Unknown'}`, uw - boxW - 10);
    tx(uaLines, ml, ay);
    ay += uaLines.length * 3.5 + 1.5;

    tx(`Time zone: America/New_York`, ml, ay);

    ay += 15;
    font('normal', 6);
    pdf.setTextColor(150);
    tx('SIGNATURE FIELD 1', ml, ay);
    pdf.setTextColor(0);
    ay += 2;
    
    if (formData.authorization.signature) {
      const auditSigUrl = resolveSignatureDataUrl(formData.authorization.signature, 72, 18);
      if (auditSigUrl) {
        pdf.addImage(auditSigUrl, 'PNG', ml, ay, 72, 18);
      }
    }

    ay += 24;
    font('normal', 6);
    pdf.setTextColor(150);
    tx('DATE FIELD 1', ml, ay);
    pdf.setTextColor(0);
    ay += 4;
    font('normal', 8);

    const sd = formData.authorization.signedDate ? new Date(formData.authorization.signedDate) : new Date();
    tx(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York' }).format(sd), ml, ay);

    ay += 10;
    hline(ay);
    ay += 8;

    font('bold', 12);
    pdf.setTextColor(100);
    tx('Event Log', ml, ay);
    pdf.setTextColor(0);
    ay += 8;

    auditLog.forEach(evt => {
      if (ay > 280) {
        pdf.addPage();
        ay = 14;
      }
      let ts = new Date();
      if (evt.timestamp && typeof evt.timestamp.toDate === 'function') {
        ts = evt.timestamp.toDate();
      } else if (evt.timestamp instanceof Date) {
        ts = ts = evt.timestamp;
      } else if (typeof evt.timestamp === 'string') {
        ts = new Date(evt.timestamp);
      }
      
      const evtTime = new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeStyle: 'short', timeZone: 'America/New_York' }).format(ts);
      font('normal', 8);
      tx(evtTime, ml, ay);

      font('bold', 8);
      tx(evt.action || '', ml + 50, ay);
      font('normal', 8);
      tx(evt.details || '', ml + 95, ay);

      ay += 6;
    });

    ay += 10;
    font('normal', 6);
    pdf.setTextColor(150);
    tx(`Document ID: ${Array.from({length: 32}, () => Math.floor(Math.random()*16).toString(16)).join('').toUpperCase()}`, ml, ay);
    pdf.setTextColor(0);
  }

  // Use a modern approach for output to accommodate environments
  return new Promise((resolve) => {
    // Determine target environment representation (Blob is native, but in node we use buffer)
    if (typeof window !== 'undefined') {
      const blob = pdf.output('blob');
      resolve(URL.createObjectURL(blob));
    } else {
      // Return base64 or binary string depending on use-case
      resolve(pdf.output('datauristring'));
    }
  });
}
